import { tryNextUrl } from '@/services/blossom.service'
import { useAtomValue } from 'jotai'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AudioPlayer from './AudioPlayer'
import VideoPlayer from './VideoPlayer'
import ExternalLink from './ExternalLink'
import { getHashFromURL } from '@nostr/tools/nipb0'

export default function MediaPlayer({
  src,
  pubkey,
  className,
  mustLoad = false
}: {
  src: string
  pubkey?: string
  className?: string
  mustLoad?: boolean
}) {
  const { t } = useTranslation()
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const [display, setDisplay] = useState(autoLoadMedia)
  const [mediaType, setMediaType] = useState<'video' | 'audio' | null>(null)
  const [error, setError] = useState(false)
  const [resolvedSrc, setResolvedSrc] = useState(src)
  const isBlossomUrl = useMemo(() => {
    if (!pubkey) return false
    if (!src.startsWith('http')) return false

    try {
      return !!getHashFromURL(src)
    } catch {
      return false
    }
  }, [src])
  const tried = useRef(new Set<string>())

  useEffect(() => {
    if (isBlossomUrl) {
      try {
        tried.current.add(new URL(src).hostname)
      } catch {
        return
      }
    }
    setResolvedSrc(src)
  }, [src, pubkey])

  useEffect(() => {
    if (autoLoadMedia) {
      setDisplay(true)
    } else {
      setDisplay(false)
    }
  }, [autoLoadMedia])

  useEffect(() => {
    if (!mustLoad && !display) {
      setMediaType(null)
      return
    }
    if (!resolvedSrc) {
      setMediaType(null)
      return
    }

    const url = new URL(resolvedSrc)
    const extension = url.pathname.split('.').pop()?.toLowerCase()

    if (extension && ['mp3', 'wav', 'flac', 'aac', 'm4a', 'opus', 'wma'].includes(extension)) {
      setMediaType('audio')
      return
    }

    let active = true
    const video = document.createElement('video')
    video.src = resolvedSrc
    video.preload = 'metadata'
    video.crossOrigin = 'anonymous'

    video.onloadedmetadata = () => {
      if (!active) return
      setError(false)
      setMediaType(video.videoWidth > 0 || video.videoHeight > 0 ? 'video' : 'audio')
    }

    video.onerror = async () => {
      if (!active) return
      if (isBlossomUrl) {
        const nextUrl = await tryNextUrl(src, pubkey!, tried.current)
        if (nextUrl) {
          setResolvedSrc(nextUrl)
          return
        }
      }
      setError(true)
    }

    return () => {
      active = false
      video.src = ''
    }
  }, [resolvedSrc, display, mustLoad, pubkey, src])

  if (error) {
    return <ExternalLink url={src} />
  }

  if (!mustLoad && !display) {
    return (
      <div
        className="text-primary hover:underline truncate w-fit cursor-pointer"
        onClick={(e) => {
          e.stopPropagation()
          setDisplay(true)
        }}
      >
        [{t('Click to load media')}]
      </div>
    )
  }

  if (!mediaType) {
    return null
  }

  if (mediaType === 'video') {
    return <VideoPlayer src={resolvedSrc} className={className} />
  }

  return <AudioPlayer src={resolvedSrc} className={className} />
}
